import { PayloadAction } from '@reduxjs/toolkit';
import { trackException } from 'app-insights/appInsightsTracking';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { candidateSearchFlattenData } from 'app/components/GlobalSearch/Candidate/SearchHelper';
import { ExceptionType } from 'app/enums/Common';
import { SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import { getAMIECategorySearch } from 'app/services/SharedServices/SharedServices';
import { TFunction } from 'i18next';
import { trackPromise } from 'react-promise-tracker';
import { call, put, takeLatest } from 'redux-saga/effects';
import { globalSearchActions, totalCountErrorThreshold } from 'store/redux-store/global-search/slice';
import { getOrderCandidateMatchActions } from './orderCandidateMatch.redux';
import { candidateMatchFlattenData } from './orderCandidateMatchHelper';
import { SortColumn } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';

const TrackWrapper = (fn, ...args) => trackPromise(fn(...args), 'order-candidate-match-call');

export function* getOrderCandidateMatch(
  action: PayloadAction<{
    activePlacements?: any;
    filter?: any;
    pageNumber: number;
    pageSize: number;
    keyword?: string;
    skillset?: [];
    unverifiedSkillsets?: [];
    orderStartDate?: any;
    divisionId?: [];
    sortedColumn?: { column: string; direction: 'asc' | 'desc' } | undefined | null;
    multiSortedColumns?: SortColumn[];
    translation: TFunction;
    recruiterIds?: [];
    candidateFilter?: any;
    skillsetFilter?: any;
    candidateIds?: [];
    cIds?: [];
    brandIds?: [];
    candidateStatusIds?: [];
    disciplineIds?: [];
    specialtyIds?: [];
    availableToStart?: any;
    lastApplicationDateRange?: any;
    nonContactedDate?: string | any;
    callBackDateRange?: any;
    LastPlacementEndDateRange?: any;
    states?: [];
    city?: [];
    zipCode?: string;
    radius?: any;
    virtualInterview?: any;
    covidVaxStatus?: any;
    license?: any;
    certification?: any;
    workHistory?: any;
  }>,
) {
  try {
    const requestBody = {
      searchType: SearchType.candidate,
      pageNumber: action.payload.pageNumber,
      pageSize: action.payload.pageSize,
      keyword: action.payload.keyword,
      skillSets: action.payload.skillset,
      unverifiedSkillsets: action.payload.unverifiedSkillsets,
      divisionId: action.payload.divisionId,
      orderStartDate: action.payload.orderStartDate,
      sortedColumn: action.payload.sortedColumn,
      multiSortedColumns: action.payload.multiSortedColumns,
      recruiterIds: action.payload.recruiterIds,
      candidateFilter: action.payload.candidateFilter,
      skillsetFilter: action.payload.skillsetFilter,
      candidateIds: action.payload.candidateIds,
      cIds: action.payload.cIds,
      brandIds: action.payload.brandIds,
      candidateStatusIds: action.payload.candidateStatusIds,
      disciplineIds: action.payload.disciplineIds,
      specialtyIds: action.payload.specialtyIds,
      availableToStart: action.payload.availableToStart,
      lastApplicationDateRange: action.payload.lastApplicationDateRange,
      nonContactedDate: action.payload.nonContactedDate,
      callBackDateRange: action.payload.callBackDateRange,
      LastPlacementEndDateRange: action.payload.LastPlacementEndDateRange,
      states: action.payload.states,
      city: action.payload.city,
      zipCode: action.payload.zipCode,
      radius: action.payload.radius,
      virtualInterview: action.payload.virtualInterview,
      covidVaxStatus: action.payload.covidVaxStatus,
      license: action.payload.license,
      certification: action.payload.certification,
      workHistory: action.payload.workHistory,
      activePlacements: action.payload.activePlacements,
    };

    const response = yield call(TrackWrapper, getAMIECategorySearch, requestBody);

    if (response?.paging?.total > totalCountErrorThreshold) {
      yield put(
        globalActions.setBanner({
          message: action.payload.translation('search.globalSearch.exceedLimitBanner'),
          severity: 'warning',
          justify: 'center',
        }),
      );
    } else {
      yield put(globalActions.closeBanner());
    }

    if (response) {
      yield put(
        getOrderCandidateMatchActions.setData({
          items: candidateMatchFlattenData(response.items),
          count: response.paging.total,
        }),
      );
      yield put(
        globalSearchActions.setSearchResults({
          items: candidateSearchFlattenData(response.items),
          count: response?.paging.total,
          miscellaneous: {},
        }),
      );
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'getOrderCandidateMatch',
        area: 'src/app/components/Order/OrderDetails/OrderCandidateMatch/store/orderCandidateMatch.saga.ts',
      },
    });
  }
}

export function* orderCandidateMatchSaga() {
  yield takeLatest(getOrderCandidateMatchActions.getOrderCandidateMatchAction.type, getOrderCandidateMatch);
}
